import cn from "classnames";
import { RefObject, useRef } from "react";

import { getBorderRadiusProps } from "@/components/BorderRadius/BorderRadius";
import { getBoxProps } from "@/components/Box/Box";
import { Stack } from "@/components/Stack/Stack";
import { useTransitionControl } from "@/contexts/TransitionContext";
import { useScrollIntoView } from "@/hooks/useScrollIntoView";
import { mergeProps } from "@/utils/reactExtensions";

import styles from "./DotStack.module.scss";

export const PAUSED_TRANSITION_DURATION = 200;
export type CellVerticalAlign = "top" | "center" | "bottom";

export type DotStackProps = React.HTMLAttributes<HTMLDivElement> & {
  /*
   * The total number of dots.
   */
  dotCount: number;

  /*
   * The selected dot.
   */
  selectedDot?: number;

  /*
   * Whether the dot stack has padding.
   */
  hasPadding?: boolean;
};

/**
 * `DotStack` component that displays a stack of dots with a selected dot.
 * If there is no enough space for the dots and if some of them are scrolled to right or left,
 * there will be an overlay on the left and right side of the dot stack, and the selected dot will be centered.
 *
 * `DotStack` component needs to be wrapped with `TransitionControlProvider` in the hierarchy appropriately to control the transition duration and pause the transition.
 *
 * ## Usage
 *
 * ```tsx
 * <TransitionControlProvider transitionDuration={7000} isTransitionPaused={false}>
 *   <DotStack dotCount={5} selectedDot={2} hasPadding={true} />
 * </TransitionControlProvider>
 * ```
 */
export const DotStack: React.FC<DotStackProps> = ({
  dotCount,
  selectedDot = 1,
  hasPadding = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);
  const selectedDotRef = useRef<HTMLDivElement>(null);

  const dotsOverflowLeft = dotsRef.current?.scrollLeft || 0;
  const overflowLeft = dotsOverflowLeft > 0;
  const overflowRight =
    dotsOverflowLeft + (containerRef?.current?.clientWidth || 0) <
    (dotsRef.current?.scrollWidth || 0);

  useScrollIntoView({ ref: selectedDotRef, dependency: selectedDot });

  return (
    <div
      ref={containerRef}
      className={cn(styles.dotStackContainer, {
        [styles.overlayLeft]: overflowLeft,
        [styles.overlayRight]: overflowRight,
        [styles.overlayBoth]: overflowLeft && overflowRight,
      })}
    >
      <div ref={dotsRef} className={styles.dotsWrapper}>
        <Stack spacing="3xs" {...mergeProps(getBoxProps({ px: hasPadding ? "xs" : "none" }))}>
          {Array.from({ length: dotCount }).map((_, index) => (
            <Dot key={index} selectedDotRef={index === selectedDot - 1 ? selectedDotRef : null} />
          ))}
        </Stack>
      </div>
    </div>
  );
};

const Dot = ({ selectedDotRef }: { selectedDotRef: RefObject<HTMLDivElement> | null }) => {
  const { transitionDuration, isTransitionPaused } = useTransitionControl() || {
    isTransitionPaused: true,
    transitionDuration: PAUSED_TRANSITION_DURATION,
  };
  const isSelected = !!selectedDotRef;

  return (
    <div
      ref={selectedDotRef}
      role="status"
      {...mergeProps(getBorderRadiusProps("rounded"), {
        className: cn(styles.dot, {
          [styles.selectedDot]: isSelected,
        }),
      })}
    >
      <div
        {...mergeProps(getBorderRadiusProps("rounded"), {
          className: cn({
            [styles.progressDot]: isSelected,
            [styles.fill]: isTransitionPaused,
          }),
          style: { animationDuration: `${transitionDuration}ms` },
        })}
      />
    </div>
  );
};
